// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { config, Hidden, Visible } from 'react-awesome-styled-grid'
import { FadeInImage } from '@components/shared/FadeInImage'
import { PlayButton } from '@components/Track/PlayButton'
import { TagPillRow } from '@components/Tag/TagPillRow'
import HorizontalMediumTrackRow from '@components/Discover/HorizontalMediumTrackRow'
import type { TrackDetailPropsType, TrackSeriesInfoType } from '../../models/Track'
import Link from 'next/link'
import { LengthInfo } from '@components/Track/LengthInfo'
import { FavoriteButton } from '@components/Track/FavoriteButton'
import { CelebrityBanner } from '@components/Series/CelebrityBanner'
import BadgeText, { BadgeTextInline } from '@components/BadgeText'
import { getComingSoonText } from '../../models/Track'
export function TrackDetail (props: TrackDetailPropsType): React.Node {
  let tags: any = props.track.tags
  if (props.track.thematicTags) {
    tags = props.track.thematicTags.concat(tags)
  }

  const badgeText = getComingSoonText(props.track.publishedAt)

  return (
    <TrackDetailContainer>
      <BackgroundImageContainer>
        <FadeInImage
          src={props.track.images.img500}
          width={500}
          height={500}
          fadeInTime={0.3}
          objectFit='cover'
          layout='fill'
        />
        <GradientOverlay/>
      </BackgroundImageContainer>
      <BackButtonAndFavorite>
        <Visible xs={true} small={false}>
          <FavoriteButton trackId={props.track.id} />
        </Visible>
      </BackButtonAndFavorite>
      <TrackDetailsHeader>
        <TrackDetailsInfoContainer>
          <Hidden xs={true} sm={false}>
          <SmallTextAndImageContainer>
            <TrackInfoContainer>
              {badgeText && <BadgeTextInline>{badgeText}</BadgeTextInline>}
              <TitleText>{props.track.title}</TitleText>
              <SeriesInfoList>
                {props.track.series.map((seriesItem: TrackSeriesInfoType, index: number): React.Node => {
                  return <SeriesContainer key={`${seriesItem.id}-${props.track.id}-${index}`}>{seriesItem.episodeInfo} <Link href={seriesItem.slug} passHref>{seriesItem.title}</Link></SeriesContainer>
                })}
              </SeriesInfoList>
              <DescriptionText>{props.track.description}</DescriptionText>
              <Hidden xs={true} sm={true} md={false}>
              <LengthContainer>
                <LengthInfo lengthText={props.track.lengthText} sexIndicatorText={props.track.sexIndicatorText} sexIndicatorTime={props.track.sexIndicatorTime}/>
              </LengthContainer>
              </Hidden>
            </TrackInfoContainer>
            <SmallImageContainer>
              {badgeText && <BadgeText>{badgeText}</BadgeText>}
              <FadeInImage src={props.track.images.img1000} width={525} height={525}/>
            </SmallImageContainer>
          </SmallTextAndImageContainer>
          </Hidden>
          <Visible xs={true} sm={false} md={false}>
            <MobileTitleContainer>
              <SmallPlayButtonContainer>
                <PlayButton {...props.track} size={60}/>
              </SmallPlayButtonContainer>
              <TitleText>{props.track.title}</TitleText>
              <SeriesInfoList>
                {props.track.series.map((seriesItem: TrackSeriesInfoType, index: number): React.Node => {
                  return <SeriesContainer key={`${seriesItem.id}-${props.track.id}-${index}`}>{seriesItem.episodeInfo} <Link href={seriesItem.slug} passHref>{seriesItem.title}</Link></SeriesContainer>
                })}
              </SeriesInfoList>

              <DescriptionText>{props.track.description}</DescriptionText>
            </MobileTitleContainer>
          </Visible>
          <TagDivider>
            <Visible xs={true} sm={true} md={false}>
            <LengthContainer>
              <LengthInfo lengthText={props.track.lengthText} sexIndicatorText={props.track.sexIndicatorText} sexIndicatorTime={props.track.sexIndicatorTime}/>
            </LengthContainer>
            </Visible>
            <TagPillRow id={props.track.id} tags={tags} small/>
          </TagDivider>
          {props.track.series?.[0]?.featured && <Visible xs={true} sm={true} md={false}><BannerContainer><CelebrityBanner {...props.track.series[0].featured}/></BannerContainer></Visible>}
          <PlayButtonContainer>
            <PlayButton {...props.track} size={60}/>
            <FavoriteButton trackId={props.track.id} />
          </PlayButtonContainer>
          {props.track.series?.[0]?.featured && <Hidden xs={true} sm={true} md={false}><BannerContainer><CelebrityBanner {...props.track.series[0].featured}/></BannerContainer></Hidden>}
        </TrackDetailsInfoContainer>
        <LargeImageContainer>
          <FadeInImage src={props.track.images.img1000} width={525} height={525}/>
        </LargeImageContainer>
      </TrackDetailsHeader>
      {props.nextInSeries && <SectionDivider><HorizontalMediumTrackRow {...props.nextInSeries}/></SectionDivider>}
      {props.previouslyInSeries && <SectionDivider><HorizontalMediumTrackRow {...props.previouslyInSeries}/></SectionDivider>}
      {props.plusYou && <SectionDivider><HorizontalMediumTrackRow {...props.plusYou}/></SectionDivider>}
      {props.moreLike && <SectionDivider><HorizontalMediumTrackRow {...props.moreLike}/></SectionDivider>}
      {props.moreLikeActor && <SectionDivider><HorizontalMediumTrackRow {...props.moreLikeActor}/></SectionDivider>}
    </TrackDetailContainer>
  )
}

const BannerContainer = styled.div`
  margin-top: 36px;
  width: 95%;
  ${(props: any): string => config(props).media.sm`
    margin-top: 24px;
  `}
`

const SeriesInfoList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 0px;
  gap: 8px;
  ${(props: any): string => config(props).media.md`
    flex-direction: row;
    gap: 16px;
  `}
`

const SeriesContainer = styled.div`
  font-family: "Good Sans Regular";
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: -0.3px;
  color: ${colors.white};
  a {
      color: ${colors.orange};
  }
  ${(props: any): string => config(props).media.md`
    font-size: 16px;
  `}
`

const BackButtonAndFavorite = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
`
const TagDivider = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 0.5px solid ${colors.white70};
  border-bottom: 0.5px solid ${colors.white70};
  padding: 12px 0px;
  ${(props: any): string => config(props).media.md`
    border: 0;
  `}
`
const PlayButtonContainer = styled.div`
  display: none;
  ${(props: any): string => config(props).media.sm`
    margin-top: 30px;
    margin-bottom: 40px;
    display: flex;
    button {
      margin-right: 20px;
    }
  `}
`
const TrackInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const SmallTextAndImageContainer = styled.div`
  display: flex;
`

const LengthContainer = styled.div`
  margin: 0px 0px 20px 0px;
`

const DescriptionText = styled.div`
  font-family: "Good Sans Regular";
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 14.85px */
  letter-spacing: -0.3px;
  color: ${(props: any): string => props.$color ? props.$color : colors.white};
  text-align: left;
  margin: 8px 0px 16px 0px;
  ${(props: any): string => config(props).media.sm`
    margin: 0px 8px 0px 15px;
    font-size: 14px;
    margin: 15px 0px 20px 0px;
    padding-right: 40px;
  `}
  ${(props: any): string => config(props).media.md`
    font-size: 16px;
    margin: 15px 0px 20px 0px;
    padding-right: 40px;
  `}
`

const TitleText = styled.h1`
  font-family: "Queens Condensed Light";
  font-size: 26px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
  width: 100%;
  color: ${colors.white};
  margin: 0;
  padding: 0;

  ${(props: any): string => config(props).media.sm`
    font-size: 36px;
  `}
  ${(props: any): string => config(props).media.md`
    width: 100%;
    font-size: 48px;
    margin: 0px 0 20px 0px;
  `}
  ${(props: any): string => config(props).media.lg`
    font-size: 60px;
  `}
`
const TrackDetailsInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${(props: any): string => config(props).media.lg`
    width: 50%;
    margin-right: 40px
  `}
  ${(props: any): string => config(props).media.xl`
    margin-right: 160px
  `}
`
const TrackDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  ${(props: any): string => config(props).media.lg`
    align-items: flex-start;
  `}
  ${(props: any): string => config(props).media.xl`
    align-items: center;
  `}
`

const LargeImageContainer = styled.div`
  display: none;
  position: relative;

  ${(props: any): string => config(props).media.lg`
    display: block;
    flex-shrink: 0;
    width: 400px;
    height: 400px;
    img {
      border-radius: 20px;
    }
  `}
  ${(props: any): string => config(props).media.xl`
    width: 500px;
    height: 500px;
  `}
`

const SmallImageContainer = styled.div`
  display: block;
  flex-shrink: 0;
  width: 250px;
  height: 250px;
  position: relative;
  img {
    border-radius: 20px;
  }
  ${(props: any): string => config(props).media.sm`
    width: 200px;
    height: 200px;
  `}
  ${(props: any): string => config(props).media.md`
    width: 250px;
    height: 250px;
  `}
  ${(props: any): string => config(props).media.lg`
    display: none;
  `}
`

const TrackDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const BackgroundImageContainer = styled.div`
  height: 375px;
  position: absolute;
  top: 0px;
  left: -20px;
  width: 100vw;
  z-index: -1;
  ${(props: any): string => config(props).media.sm`
    display: none
  `}
`

const GradientOverlay = styled.div`
  background: linear-gradient(
    0deg,
    ${(props: any): string => colors.midnightBlue},
    ${(props: any): string => colors.midnightBlue0} 100%
  );
  bottom: -1px;
  height: 160px;
  position: absolute;
  width: 100%;
`

const MobileTitleContainer = styled.div`
   margin-top: 280px;
    position: relative;
`

const SmallPlayButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 100px;
  text-align: right;
`

const SectionDivider = styled.div`
  margin-top: 36px;
  ${(props: any): string => config(props).media.md`
    margin-top: 60px;
  `}
`
